import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { v } from "../../../util";
import type { PipelineItem } from "../Pipeline/use-pipelines";
import { Container } from "./styles";
import { Button, useToast } from "../..";
import { useAppSelector } from "../../../redux/hooks";
import usePipelinesApi from "../Pipeline/use-pipelines";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";

type Props = {
  pipeline: PipelineItem | null;
  onSuccess: () => void;
  onClose: () => void;
};

type UpdatePipelineFormValues = {
  name: string;
  workspaceId: number | null;
};

const UpdatePipeline = ({ pipeline, onSuccess, onClose }: Props) => {
  const editPipelineSchema = v.object({
    name: v.string().superRefine(v.nonempty()),
    workspaceId: v
      .number()
      .nullable()
      .refine((value) => value != null, {
        message: "Workspace é obrigatório"
      })
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<UpdatePipelineFormValues>({
    defaultValues: {
      name: "",
      workspaceId: null
    },
    resolver: zodResolver(editPipelineSchema),
    shouldFocusError: true
  });

  const { createPipeline, editPipeline } = usePipelinesApi();
  const toast = useToast();
  const allWorkspaces = useAppSelector(
    (state) => state.workspaces.userWorkspaces
  );
  const { user, company, hasAnyPermission } = useAuth() as AuthContextType;
  const companyId = user.admin ? company?.id : user.companyId;

  const workspaceOptions = allWorkspaces.map((ws) => ({
    label: ws.name,
    value: ws.id
  }));

  useEffect(() => {
    reset({
      name: pipeline?.name ?? "",
      workspaceId: pipeline?.workspaceId ?? null
    });
  }, [pipeline, reset]);

  const handlePipeline = handleSubmit(async (data) => {
    try {
      if (pipeline?.id) {
        const canEditPipeline = user.admin || hasAnyPermission("pipeline_edit");

        if (!canEditPipeline) {
          toast?.current?.show({
            life: 5000,
            severity: "warn",
            summary: "Aviso",
            detail: "Você não tem permissão para editar pipeline."
          });
          return;
        }

        await editPipeline(pipeline.id, data.name, data.workspaceId!);

        toast?.current?.show({
          life: 3000,
          severity: "success",
          summary: "Sucesso!",
          detail: "Pipeline atualizado com sucesso!"
        });
      } else {
        const canCreatePipeline =
          user.admin || hasAnyPermission("pipeline_create");

        if (!canCreatePipeline) {
          toast?.current?.show({
            life: 5000,
            severity: "warn",
            summary: "Aviso",
            detail: "Você não tem permissão para criar pipeline."
          });
          return;
        }

        await createPipeline(companyId, data.name, data.workspaceId!);

        toast?.current?.show({
          life: 3000,
          severity: "success",
          summary: "Sucesso!",
          detail: "Pipeline criado com sucesso!"
        });
      }

      onSuccess();
    } catch (error: any) {
      const backendMessage =
        error?.message ||
        error?.response?.data?.message ||
        (pipeline?.id
          ? "Erro ao atualizar pipeline."
          : "Erro ao criar pipeline.");

      toast?.current?.show({
        life: 5000,
        severity: "error",
        summary: "Erro",
        detail: backendMessage
      });
    }
  });

  return (
    <Container>
      <div className="divider">
        <div className="content">
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <InputText {...field} placeholder="Nome do funil" />
            )}
          />
          {errors.name?.message ? (
            <div style={{ color: "#AA3939", marginTop: "5px" }}>
              {errors.name.message}
            </div>
          ) : null}
        </div>

        <div className="content">
          <Controller
            name="workspaceId"
            control={control}
            render={({ field }) => (
              <Dropdown
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                options={workspaceOptions}
                optionLabel="label"
                optionValue="value"
                appendTo="self"
                placeholder="Workspace"
                emptyMessage="Nenhum workspace disponível"
                style={{
                  display: "flex",
                  alignItems: "center",
                  height: "38px"
                }}
                panelStyle={{ width: "100%" }}
              />
            )}
          />
          {errors.workspaceId?.message ? (
            <div style={{ color: "#AA3939", marginTop: "5px" }}>
              {errors.workspaceId.message}
            </div>
          ) : null}
        </div>
      </div>

      <div className="buttons">
        <Button label="VOLTAR" onClick={onClose} className="no-hover-button" />
        <Button
          onClick={handlePipeline}
          style={{
            background: user.plataform.mainColor
              ? user.plataform.mainColor
              : "#2624CD",
            borderColor: user.plataform.mainColor
              ? user.plataform.mainColor
              : "#2624CD",
            color: "#fff"
          }}
        >
          SALVAR
        </Button>
      </div>
    </Container>
  );
};

export default UpdatePipeline;
